import React, { useState, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { projects } from '../../data/projectsData';
import './ProjectSlider.css';
import './projectslider-responsive.css';

const ProjectSlider: React.FC = () => {
  // Select the top 4 projects to feature in the slider
  const featuredProjects = projects.slice(0, 4);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  const navigate = useNavigate();

  const paginate = useCallback((newDirection: number) => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) {
        return featuredProjects.length - 1;
      } else if (nextIndex >= featuredProjects.length) {
        return 0;
      }
      return nextIndex;
    });
  }, [featuredProjects.length]);

  const handleDotClick = (index: number) => {
    if (index !== currentIndex) {
      setCurrentIndex(index);
    }
  };

  const handleCardClick = (projectId: number) => {
    navigate(`/project/${projectId}`);
  };

  // Get visible projects for the slider (current and surrounding ones)
  const getVisibleProjects = () => {
    const visibleProjects = [];
    
    // Add previous project
    const prevIndex = currentIndex === 0 ? featuredProjects.length - 1 : currentIndex - 1;
    visibleProjects.push({ ...featuredProjects[prevIndex], position: 'prev' });
    
    // Add current project
    visibleProjects.push({ ...featuredProjects[currentIndex], position: 'current' });
    
    // Add next project
    const nextIndex = currentIndex === featuredProjects.length - 1 ? 0 : currentIndex + 1;
    visibleProjects.push({ ...featuredProjects[nextIndex], position: 'next' });
    
    return visibleProjects;
  };

  // Animation variants for the container
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <section className="project-slider-section py-5" id="featured-projects" ref={ref}>
      <div className="radial-glow-slider" />
      <Container className="py-4">
        <motion.div 
          className="project-slider-header text-center mb-5"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          <div className="title-container mb-2">
            <h2>Featured Projects</h2>
          </div>
          <p className="text-secondary subtitle">A showcase of my premium full-stack and intelligent agentic products.</p>
        </motion.div>

        <div className="project-slider-container position-relative">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "visible"}
            className="row justify-content-center"
          >
            {getVisibleProjects().map((project, index) => (
              <div 
                key={`${project.id}-${index}`}
                className={`col-md-4 project-slide ${project.position}`}
              >
                <motion.div 
                  className="project-slide-card h-100 d-flex flex-column justify-content-between cursor-pointer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -6, borderColor: 'var(--accent-color)', boxShadow: '0 15px 35px rgba(99, 102, 241, 0.1)' }}
                  onClick={() => handleCardClick(project.id)}
                >
                  <div className="project-slide-image position-relative overflow-hidden">
                    <img src={project.image} alt={project.title} className="w-100 object-fit-cover" />
                    <div className="project-slide-overlay d-flex align-items-center justify-content-center">
                      <div className="project-slide-buttons">
                        <span className="btn btn-sm btn-light font-weight-bold px-3 py-1.5 rounded-pill">
                          View Project
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="project-slide-info p-4 d-flex flex-column flex-grow-1">
                    <span className="project-slide-category text-uppercase font-weight-bold mb-2">
                      {project.category.slice(0, 2).join(' & ')}
                    </span>
                    <h4 className="project-slide-title font-display mb-2">{project.title}</h4>
                    <p className="project-slide-desc text-secondary mb-3">{project.description}</p>
                    <div className="project-slide-tech d-flex flex-wrap gap-2 mt-auto">
                      {project.technologies.slice(0, 2).map((tech, i) => (
                        <span key={i} className="tech-tag">{tech}</span>
                      ))}
                      {project.technologies.length > 2 && (
                        <span className="tech-tag font-weight-bold">+{project.technologies.length - 2}</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </motion.div>

          {/* Slider controls */}
          <div className="slider-nav-buttons">
            <motion.button
              className="slider-nav-button d-flex align-items-center justify-content-center rounded-circle"
              onClick={() => paginate(-1)}
              whileHover={{ scale: 1.1, background: 'var(--accent-color)', color: '#ffffff' }}
              whileTap={{ scale: 0.9 }}
            >
              ←
            </motion.button>
            <motion.button
              className="slider-nav-button d-flex align-items-center justify-content-center rounded-circle"
              onClick={() => paginate(1)}
              whileHover={{ scale: 1.1, background: 'var(--accent-color)', color: '#ffffff' }}
              whileTap={{ scale: 0.9 }}
            >
              →
            </motion.button>
          </div>

          <div className="slider-dots text-center mt-4">
            {featuredProjects.map((_, index) => (
              <motion.div
                key={index}
                className={`slider-dot mx-1 d-inline-block rounded-circle ${index === currentIndex ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
                whileHover={{ scale: 1.25 }}
              />
            ))}
          </div>

          <motion.div 
            className="view-all-button text-center mt-5"
            initial={{ opacity: 0, y: 15 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
            transition={{ delay: 0.3 }}
          >
            <Link to="/projects">
              <motion.button
                className="btn btn-outline-primary px-5 py-2.5"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                View All Projects
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default ProjectSlider;
